import { motion } from "framer-motion";
import {
  Eye,
  AlertCircle,
  MessageCircleQuestion,
  GitBranch,
  Ban,
  Lightbulb,
  PenLine,
  ListChecks,
  ArrowUpRight,
} from "lucide-react";
import { AMAZON_URL } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const QUESTIONS = [
  "How old is this outstanding check?",
  "Do I know why it has not cleared?",
  "Did the payee receive the check?",
  "Was the check replaced or canceled?",
  "Is the payment still owed?",
];

const CAUSES = [
  "The payee has not deposited the check",
  "The check was lost or never received",
  "A replacement payment was issued",
  "The check was voided but remains in the books",
  "The transaction was recorded incorrectly",
];

const NOT_TO_DO = [
  "Do not delete an outstanding check simply because it is old. Removing it without knowing what happened can change the bank balance in your books without correcting the underlying problem.",
  "Do not issue another check until you determine what happened to the original payment. If both checks are eventually deposited, the business could pay the same obligation twice.",
  "Do not leave old checks outstanding indefinitely either. An old check needs an explanation and, when necessary, action based on what actually happened.",
];

const Block = ({ icon: Icon, title, testId, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: EASE }}
    className="mt-12"
    data-testid={testId}
  >
    <div className="flex items-center gap-3.5">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#20C9C3]/40 bg-[#20C9C3]/8">
        <Icon className="h-[18px] w-[18px] text-[#14A8A3]" strokeWidth={1.8} />
      </span>
      <h3 className="font-display text-xl sm:text-2xl font-semibold text-[#1E2A2B]">
        {title}
      </h3>
    </div>
    <div className="mt-5">{children}</div>
  </motion.div>
);

const Prose = ({ children }) => (
  <div className="space-y-4 text-[15px] sm:text-base leading-relaxed text-[#1E2A2B]/70">
    {children}
  </div>
);

export const FreeSample = () => (
  <section
    id="sample"
    data-testid="sample-section"
    className="relative overflow-hidden bg-white py-20 sm:py-28"
  >
    <div className="grain pointer-events-none absolute inset-0 opacity-[0.4]" />
    <div className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#20C9C3]/8 blur-[110px]" />

    <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="text-center"
      >
        <div className="mb-5 flex items-center justify-center gap-3">
          <span className="h-px w-10 bg-[#C5A253]" />
          <span className="text-[12px] font-bold uppercase tracking-[0.24em] text-[#14A8A3]">
            Free Sample
          </span>
          <span className="h-px w-10 bg-[#C5A253]" />
        </div>
        <h2
          data-testid="sample-heading"
          className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E2A2B]"
        >
          Read a Red Flag From the Book
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#1E2A2B]/65">
          This is one complete red flag, exactly as it appears inside. Every
          flag in the book follows this same practical structure.
        </p>
      </motion.div>

      <motion.article
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
        data-testid="sample-card"
        className="relative mt-14 rounded-[20px] bg-[#F6F5F0] border-t-4 border-t-[#20C9C3] p-7 sm:p-12 shadow-[0_35px_70px_-30px_rgba(30,42,43,0.25)]"
      >
        <div className="flex items-start gap-5">
          <span className="font-display text-6xl sm:text-7xl font-black leading-none text-[#20C9C3]">
            07
          </span>
          <div className="pt-1.5">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#C5A253]">
              Red Flag #7
            </p>
            <h3
              data-testid="sample-flag-title"
              className="mt-1.5 font-display text-2xl sm:text-3xl font-bold text-[#1E2A2B]"
            >
              Outstanding Checks
            </h3>
          </div>
        </div>

        <Block icon={Eye} title="What You Might Notice" testId="sample-notice">
          <Prose>
            <p>
              Your bank reconciliation shows checks written by your business
              that have not cleared the bank. Some may be recent, while others
              have remained outstanding for several months.
            </p>
            <p>
              You may recognize some of the checks and know why they have not
              cleared. Others may be old enough that you no longer remember
              what happened after the check was written.
            </p>
          </Prose>
        </Block>

        <Block icon={AlertCircle} title="Why It Matters" testId="sample-matters">
          <Prose>
            <p>
              An outstanding check represents money recorded as paid in your
              books but not withdrawn from your bank account. That difference
              is normal while the check is waiting to clear.
            </p>
            <p>
              The problem begins when a check remains outstanding long enough
              that you no longer know why. The payment may never have reached
              the payee, or something may have happened after the check was
              issued.
            </p>
            <p>
              Old outstanding checks can also make the cash available in your
              books different from what you see at the bank. If the check is
              still valid, that money may eventually leave the account even
              though it has been sitting there for months.
            </p>
            <p>
              Until you determine what happened, you cannot know whether the
              check should remain outstanding or whether the books need to be
              corrected.
            </p>
          </Prose>
        </Block>

        <Block
          icon={MessageCircleQuestion}
          title="Questions to Ask Yourself"
          testId="sample-questions"
        >
          <ul className="space-y-3.5">
            {QUESTIONS.map((q, i) => (
              <li key={i} className="flex items-start gap-3.5">
                <span className="mt-[3px] h-[18px] w-[18px] shrink-0 rounded-[4px] border-2 border-[#20C9C3]" />
                <span className="text-[15px] sm:text-base text-[#1E2A2B]/75">
                  {q}
                </span>
              </li>
            ))}
          </ul>
        </Block>

        <Block icon={GitBranch} title="Common Causes" testId="sample-causes">
          <ul className="space-y-3">
            {CAUSES.map((c, i) => (
              <li key={i} className="flex items-start gap-3.5">
                <span className="mt-[9px] h-[3px] w-5 shrink-0 bg-[#C5A253]" />
                <span className="text-[15px] sm:text-base text-[#1E2A2B]/75">
                  {c}
                </span>
              </li>
            ))}
          </ul>
        </Block>

        <Block icon={Ban} title="What Not to Do" testId="sample-not-to-do">
          <div className="space-y-4">
            {NOT_TO_DO.map((t, i) => (
              <p
                key={i}
                className="border-l-[3px] border-[#C5A253] pl-5 text-[15px] sm:text-base leading-relaxed text-[#1E2A2B]/75"
              >
                {t}
              </p>
            ))}
          </div>
        </Block>

        <Block
          icon={Lightbulb}
          title="Professional Insight"
          testId="sample-insight"
        >
          <div className="rounded-[14px] bg-white p-6 sm:p-8 border-l-[3px] border-[#20C9C3]">
            <Prose>
              <p>
                Outstanding checks are normal. What gets my attention is a
                check that remains outstanding much longer than expected. At
                that point, I want to know whether the payment is still valid
                and whether the person or business it was issued to ever
                received it.
              </p>
              <p>
                The age of the check matters because the longer it remains
                outstanding, the less likely it is to be normal timing. A check
                from last week and a check from last year should not be treated
                the same way.
              </p>
              <p>
                I also want to know whether anyone has followed up on the
                payment. The books may show that the business paid the expense,
                but an old outstanding check can mean the money never actually
                reached the person or business it was intended for.
              </p>
              <p>
                The amount can matter too. One forgotten check may have little
                effect on the overall bank balance, while several old checks or
                one large check can make the amount shown in the books very
                different from the money actually available.
              </p>
              <p>
                Old checks can also uncover other bookkeeping problems.
                Sometimes the original payment was replaced, voided, or handled
                another way, but the books were never updated. Until you trace
                what happened, the outstanding check can continue affecting the
                account.
              </p>
            </Prose>
          </div>
        </Block>

        <Block icon={PenLine} title="Your Notes" testId="sample-notes">
          <div className="space-y-7 pt-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className="border-b border-[#1E2A2B]/20" />
            ))}
          </div>
        </Block>

        <Block icon={ListChecks} title="Take Action" testId="sample-action">
          <Prose>
            <p>
              Start with the oldest outstanding check. Find the original
              transaction in your books and confirm who was paid, when the
              check was issued, and what the payment was for.
            </p>
            <p>
              Check the bank statements after the issue date to make sure the
              check truly never cleared. If you find that it did clear, correct
              the bookkeeping so the check is no longer shown as outstanding.
            </p>
            <p>
              If the check never cleared, determine what happened to the
              payment. Contact the payee when necessary and confirm whether the
              check was received and whether the money is still owed.
            </p>
            <p>
              If the payment is still owed and a replacement check is needed,
              make sure the original check cannot also be deposited. Then
              record the replacement in a way that preserves the original
              expense or liability rather than recording the same expense
              again.
            </p>
            <p>
              If the money is no longer owed, determine why before changing the
              books. An old outstanding check should not simply be deleted
              because removing it may affect the account the original payment
              was recorded against.
            </p>
            <p>
              Work through the remaining outstanding checks the same way. Once
              the old items are resolved, pay attention to outstanding checks
              during each reconciliation so they do not sit unnoticed for
              months or years.
            </p>
          </Prose>
        </Block>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mt-14 border-t border-[#1E2A2B]/10 pt-10 text-center"
        >
          <p className="font-display text-xl sm:text-2xl italic text-[#1E2A2B]">
            This was Red Flag #7 of{" "}
            <span className="not-italic font-bold text-[#14A8A3]">50</span>.
          </p>
          <a
            href={AMAZON_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sample-buy-button"
            className="group mt-6 inline-flex items-center gap-2.5 rounded-full bg-[#20C9C3] px-9 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white shadow-[0_16px_38px_rgba(32,201,195,0.35)] transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#14A8A3] hover:-translate-y-1 hover:shadow-[0_22px_46px_rgba(32,201,195,0.45)]"
          >
            Buy the Book on Amazon
            <ArrowUpRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </motion.div>
      </motion.article>
    </div>
  </section>
);
